<template>
  <div class="project-results" v-if="project">
    <div class="header">
      <div class="controls-top">
        <div class="group-selector">
          <multi-select-component
            :label="$t('selected_group')"
            :configurationOptions="{ 'label-side': true,  'allow-empty': false}"
            mode="single"
            v-model="mainGroup"
            :options="availableGroupsWithAll"
          ></multi-select-component>
        </div>
        <div class="separator"></div>
        <div class="mode-btns">
          <div class="mode-label">{{ $t("view_by") }}</div>
          <div class="btns-container">
            <div class="mode-btn" :class="{ selected: chartMode === 'scatter' }">
              <input
                type="radio"
                id="scatter"
                value="scatter"
                v-model="chartMode"
              />
              <label for="scatter">{{ $t("trials_scatter_mode") }}</label>
            </div>
            <div class="mode-btn" :class="{ selected: chartMode === 'heatmap' }">
              <input
                type="radio"
                id="heatmap"
                value="heatmap"
                v-model="chartMode"
              />
              <label for="heatmap">{{ $t("trials_heatmap_mode") }}</label>
            </div>
            <div class="mode-btn" :class="{ selected: chartMode === 'list' }">
              <input type="radio" id="list" value="list" v-model="chartMode" />
              <label for="list">{{ $t("trials_list_mode") }}</label>
            </div>
          </div>
        </div>
        <div class="separator"></div>
        <div class="selection-modes">
          <div class="selection-label">
            Filter/Select
          </div>
          <div class="selection-container">
            <div
              class="selector"
              v-if="chartMode === 'scatter' || chartMode === 'heatmap'"
            >
              <div
                class="selection-btn rect"
                :class="{
                  active: brushMode === 'rect',
                  innactive: brushMode && brushMode !== 'rect',
                }"
                @click="brushMode = brushMode !== 'rect' ? 'rect' : null"
              ></div>
            </div>
            <div
              class="selector"
              v-if="chartMode === 'scatter' || chartMode === 'heatmap'"
            >
              <div
                class="selection-btn poly"
                :class="{
                  active: brushMode === 'poly',
                  innactive: brushMode && brushMode !== 'poly',
                }"
                @click="brushMode = brushMode !== 'poly' ? 'poly' : null"
              ></div>
            </div>
            <div class="selector">
              <div
                class="selection-btn params"
                :class="{
                  active: brushMode === 'params',
                  innactive: brushMode && brushMode !== 'params',
                }"
                @click="openParamsSelection"
              ></div>
            </div>
            <div v-if="selection" class="create-group-btn btn btn--primary" @click="openCreateGroupModal">
              {{ $t("create_group_from_sel") }} ({{ selection.length }})
            </div>
            <div v-if="selection" class="deselect btn btn--primary" @click="selection=null; brushMode = null">
              Clear selection
            </div>
          </div>
          
        </div>
        <div class="align-right">
          <div class="mode-label"></div>
          <div class="sort-selector" v-if="chartMode === 'list'">
            <multi-select-component
              :label="$t('sort_by')"
              :configurationOptions="{ 'label-inside': true }"
              mode="single"
              v-model="sortAttr"
              :options="availableAttrsWithGender"
            ></multi-select-component>
            <div
              class="sort-arrow down"
              :class="sortingDirection === 'down' ? 'selected' : 'unselected'"
              @click="sortingDirection = 'down'"
            ></div>
            <div
              class="sort-arrow up"
              :class="sortingDirection === 'up' ? 'selected' : 'unselected'"
              @click="sortingDirection = 'up'"
            ></div>
            <div class="separator"></div>
          </div>
          <div
            class="btn btn--primary details-button"
            @click="detailsOpen = true"
          >
            {{ $t("explore_details") }}
          </div>
          <modal-custom-report name="modal-custom-report"  width="80%" height="70%" :availableAttrsX="availableAttrsX.filter(attr => attr.label != 'Subject')" :availableAttrsY="availableAttrsY" :availableTags="tagNames" @export="generateCustomReport"></modal-custom-report>
          <modal-loading name="loading-modal"  width="80%" height="60%"></modal-loading>
          <v-popover class="add-config-popover" :disabled="generatingReport">
            <template slot="popover">
                <div class="option-popover">
                  <option-card
                    v-for="reportOption in availableReportOptions" :key="reportOption.key"
                    optionType="report-template"
                    :active="true"
                    :option="reportOption"
                    :addLabel="reportOption.buttonLabel"
                    @addOption="generateReport(reportOption)">
                  </option-card>
                  <option-card 
                    optionType="report-template"
                    :active="true"
                    :option="customReportOption"
                    addLabel="Configure"
                    @addOption="openCustomReportModal()">
                  </option-card>
                </div>
              </template>
            <div v-bind:class="chartMode == 'scatter' ? '':'disabled'" class="btn btn--primary details-button">
              Export Report
            </div>
          </v-popover>
          <div class="btn btn--primary details-button" @click="exportCSV()">
            {{ $t("export_csv") }}
          </div>
          <v-popover v-if="!importedDataIsLoaded">
            <template slot="popover">
                <div class="option-popover">
                  <div class="import-paragraph" style="margin-bottom: 10px">
                    Imported data must contain a subject ID for every row and at least all parameters present in the simulation set axes.
                  </div>
                  <form id="csvForm">
                    <input type="file" id="csvFile" accept=".csv" style="margin-bottom: 15px"/>
                    <div class="btn btn--primary details-button" @click="importCSVFile()">Submit File</div>
                  </form>
                </div>
              </template>
            <div v-bind:class="chartMode == 'scatter' ? '':'disabled'" class="btn btn--primary details-button">
              Import Data
            </div>
          </v-popover>
          <div v-else class="btn btn--primary details-button" @click="removeImportedData()">
              Remove Imports
            </div>
        </div>
        
      </div>
    </div>
    <div class="results-body">
      <div class="left-wrapper">
        <div v-if="!generatingReport" class="content">
          <loading-overlay v-if="isLoading"></loading-overlay>
          <trials-scatter
            id="plot-screenshot"
            v-if="!isLoading && (chartMode === 'scatter' || chartMode === 'heatmap')"
            :data="resultsWithoutNullAttributes"
            :generatingReport="generatingReport"
            :colorTag="colorTag"
            :symbolTag="symbolTag"
            :colorGroups="colorGroups"
            @update="colorGroups=$event"
            :xAttr="xAttr"
            :yAttr="yAttr"
            :sortAttr="sortAttr"
            :sizeAttr="this.chartMode === 'heatmap' ? null : sizeAttr"
            :defaultSize="35"
            :alphaAttr="this.chartMode === 'heatmap' ? null : alphaAttr"
            :mode="chartMode"
            :color="color"
            :brushMode="brushMode"
            @brush="brushMode=null"
            :selection="mouseoverSubjects ? mouseoverSubjects : selection"
            @select="selection = $event"
            v-model="highlightedSubject"
          />
          <result-patients-list
            v-if="!isLoading & chartMode === 'list'"
            :results="sortedGroupedResults"
            :selection="selection"
            @select="selection = $event"
          ></result-patients-list>
        </div>
        <div v-else class="content" style="width: 800px; background-color: white; fill: white"> 
          <trials-scatter
            style="height: 600px"
            id="plot-screenshot"
            v-if="chartMode === 'scatter' || chartMode === 'heatmap'"
            :data="resultsWithoutNullAttributes"
            :generatingReport="generatingReport"
            :colorTag="colorTag"
            :symbolTag="symbolTag"
            :colorGroups="colorGroups"
            @update="colorGroups=$event"
            :xAttr="xAttr"
            :yAttr="yAttr"
            :sortAttr="sortAttr"
            :sizeAttr="this.chartMode === 'heatmap' ? null : sizeAttr"
            :defaultSize="15"
            :alphaAttr="this.chartMode === 'heatmap' ? null : alphaAttr"
            :mode="chartMode"
            :color="color"
            :brushMode="brushMode"
            @brush="brushMode=null"
            :selection="mouseoverSubjects ? mouseoverSubjects : selection"
            @select="selection = $event"
            v-model="highlightedSubject"
          />
        </div>
      </div>
      <div
        class="right-wrapper"
        v-if="chartMode === 'scatter' || chartMode === 'heatmap'"
      >
        <div class="main-group-title">
          <div class="title">
            {{ currentMainGroup.name }}
            <div class="group-info" v-if="!isLoading">
              {{results.length}} {{results.length == 1 ? 'case' : 'cases'}}
            </div>
          </div>
          <div class="group-actions" v-if="!currentMainGroup.preventDelete">
            <div class="action-button active remove-button" @click="deleteGroup"></div>
          </div>
        </div>
        <template v-if="chartMode === 'scatter'">
          <div class="attr-selectors control-block half-selectors">
            <div class="selector dropdown">
              <multi-select-component
                :label="$t('x_axis')"
                mode="single"
                v-model="xAttr"
                :options="availableAttrsX"
                :configurationOptions="{ 'allow-empty': false }"
              ></multi-select-component>
            </div>
            <div class="selector dropdown">
              <multi-select-component
                :label="$t('y_axis')"
                mode="single"
                v-model="yAttr"
                :options="availableAttrsY"
                :configurationOptions="{ 'allow-empty': false }"
              ></multi-select-component>
            </div>
          </div>
          <div class="group-selectors control-block">
            <multi-select-component
              :label="'Color Grouping by'"
              mode="single"
              v-model="colorTag"
              :options="this.tagNames"
            ></multi-select-component>
            <div
              class="compare-legend" id="color-legend"
            >
              <div
                class="legend-item compare"
                v-for="g in colorGroups"
                :key="g.key"
                @mouseover="mouseoverSubjects = g.subjects"
                @mouseout="mouseoverSubjects = null"
              >
                <div
                  class="legend-icon"
                  :style="{ backgroundColor: g.color }"
                />
                <div class="legend-label">{{ g.name }}</div>
                <div class="legend-count">
                  {{ g.subjects.length }}
                </div>
              </div>
            </div>
          </div>
          <div class="group-selectors control-block">
            <multi-select-component
              :label="'Symbol Grouping by'"
              mode="single"
              v-model="symbolTag"
              :options="this.tagNames"
            ></multi-select-component>
            <div
              class="compare-legend" id="symbol-legend"
              v-if="symbolGroups.length > 0"
            >
              <div
                class="legend-item compare"
                v-for="g in symbolGroups"
                :key="g.key"
                @mouseover="mouseoverSubjects = g.subjects"
                @mouseout="mouseoverSubjects = null"
              >
                <div class="legend-icon">
                    <svg :class="`legend-icon-${g.symbol}`" ></svg>
                </div>
                <div class="legend-label">{{ g.name }}</div>
                <div class="legend-count">
                  {{ g.subjects.length }}
                </div>
              </div>
            </div>
          </div>
          <div v-if="this.resultsWithNullAttribute.length > 0" class="null-attributes-container">
            <div class="group-info" ><b>{{ this.resultsWithNullAttribute.length }} Arrythmic Cases</b></div>
            <div class="group-info" >Attribute could not be calculated for the following cases due to arrythmia.</div>
            <div class="subjects">
              <div class="subject"
                v-for="res in this.resultsWithNullAttribute"
              >
                <div @click="goToResultDashboard(res)"><b class="subject-id">{{ res.subject.templateId }} </b> ({{ res.trialMeta.name }})</div>
              </div>
            </div>
          </div>
          <div v-if="showRegression" class="regression-container">
             <div id="regression-image" v-if="colorGroups.length && 'regressionData' in colorGroups[0]">
              <div class="title" >Linear Regression</div>
              <div v-if="colorTag" class="group-name" >Grouping by: {{ colorTag }}</div>
              <div v-else class="group-name" >Whole population</div>
              <div class="regression-item">
                <div class="label name">Group</div>
                <div class="label equation">Equation</div>
                <div class="label r2">R<sup>2</sup></div>
              </div>
              <div
                  class="regression-item"
                  v-for="g in colorGroups"
                  :key="g.key"
                  @mouseover="mouseoverSubjects = g.subjects"
                  @mouseout="mouseoverSubjects = null"
                >
                  <div class="item icon" @click="showOrHideRegression(g)">
                    <div class="base-circle">
                      <div class="selected-option-mark" :style="{ backgroundColor: g.color, opacity: g.paintRegression ? 1 : 0.3 }"></div>
                    </div>
                  </div>
                  <div class="item equation">y = {{g.regressionData.m}}x + {{g.regressionData.n}}</div>
                  <div class="item r2">{{ g.regressionData.r2 }}</div>
                </div>
            </div>
          </div>
        </template>
        <template v-if="chartMode === 'heatmap'">
          <div class="attr-selectors control-block">
            <div class="selector dropdown">
              <multi-select-component
                :label="$t('sort_by')"
                mode="single"
                v-model="sortAttr"
                :options="availableAttrs"
                :configurationOptions="{ 'allow-empty': false }"
              ></multi-select-component>
            </div>
          </div>
          <div class="attr-selectors control-block">
            <div class="selector dropdown">
              <multi-select-component
                :label="$t('color_scale_attr')"
                mode="single"
                v-model="colorAttr"
                :options="availableAttrs"
                :configurationOptions="{ 'allow-empty': false }"
              ></multi-select-component>
              <div class="alpha-legend" v-if="colorAttr">
                <color-legend :colorScale="color_legendScale"></color-legend>
              </div>
            </div>
          </div>
          
        </template>
      </div>
    </div>

    <!-- <vue-draggable-resizable v-if="detailsOpen" :w="detailsPopupLayout.w" :h="detailsPopupLayout.h" :x="detailsPopupLayout.x" :y="detailsPopupLayout.y" :minWidth="400" :minHeight="200" :parent="true" :active="true"> -->
    <result-patients-panel
      v-if="detailsOpen"
      class="floating-panel"
      :caseList="mainResults"
      :selectionList="selection || (highlightedSubject?[highlightedSubject]:null)"
      :layout="detailsPopupLayout"
      :mainGroup="currentMainGroup.name"
      :groupsAndTrials="availableGroupsAndTrials"
      :statisticalTestResults="statisticalTestResults"
      @close="detailsOpen = false"
      v-model="highlightedSubject"
      @statisticalAnalysisLoaded="updateStatisticalAnalysis"
    ></result-patients-panel>
    <!-- </vue-draggable-resizable> -->

    <!-- <modal-selection-params
      name="selection-params"
      @complete="(res) => (selection = res)"
      @cancel="selection = null"
    ></modal-selection-params> -->
    <modal-selection-params
      name="selection-params"
      @filterConfig="applyFilter"
      @cancel="selection = null"
    ></modal-selection-params>

    <modal-create-group name="create-group" :project="project" @created="refreshGroups"></modal-create-group>
  </div>
  <loading-overlay v-else></loading-overlay>
</template>

<script>
import TrialsScatter from '@/components/trial/TrialsScatter'
import ResultPatientsList from '@/components/ResultPatientsList'
import ResultPatientsPanel from '@/components/ResultPatientsPanel'
import Api from '@/api'
import nextTick from 'vue'
import d3 from '@/utils/d3importer'
import {getNestedAttr} from '@/utils'
import MultiSelectComponent from '@/components/MultiSelectComponent'
import ColorLegend from '@/components/commons/ColorLegend'
import SizesLegend from '@/components/commons/SizesLegend'
import { std, mean } from '@/utils/Math'
import LoadingOverlay from '@/components/commons/LoadingOverlay'
import ModalSelectionParams from '@/components/modals/ModalSelectionParams'
import ModalCreateGroup from '@/components/modals/ModalCreateGroup'
import { CSVExportTrial, CSVExportProject, CSVExportGroup } from '@/utils/csvExporter'
import _ from 'underscore'
import reportGenerator from '@/utils/report/reportGenerator.js'
import reportTemplateHandler from '@/utils/report/reportTemplateHandler.js'
import domtoimage from 'dom-to-image';

import widgetContent from '@/mixins/widgetContent'
import ModalCustomReport from '@/components/modals/ModalCustomReport.vue'
import OptionCard from '@/components/commons/OptionCard'
import ModalLoading from '@/components/modals/ModalLoading.vue'
import store from '@/store'

const COLORS = {
  mainGroup: '#4760DC',
  compareGroup: '#EAA226'
}

const COMPARE_COLORS = [
  '#4760DC', '#EAA226', '#22df44', '#bc5090', '#ff6361', '#cab2d6', '#6a3d9a'
]
const COMPARE_SYMBOLS = [
  'circle', 'cross', 'square', 'triangle', 'diamond', 'wye', 'star'
]
export default {
  name: 'ProjectResults',
  props: ['project', 'projectSubjectTags'],
  mixins: [widgetContent],
  components: { TrialsScatter, ResultPatientsList, ResultPatientsPanel, MultiSelectComponent, ColorLegend, SizesLegend, LoadingOverlay, ModalSelectionParams, ModalCreateGroup, ModalCustomReport, OptionCard, ModalLoading },
  data(){
    return {
      availableReportOptions: [],
      customReportOption: {
          name: 'Custom analysis',
          description: 'Customize the plots you want to show in the report',
      },
      mainGroup:-1,
      xAttr: null,
      sortAttr: 'subject.id',
      yAttr: null,
      colorAttr: null,
      sortingDirection: 'down',
      alphaAttr: null,
      sizeAttr: null,
      compareGroupsId: [],
      chartMode: 'scatter',
      brushMode: null,
      selection: null,
      COLORS: COLORS,
      detailsOpen: false,
      detailsPopupLayout: {},
      highlightedSubject: null,
      assignedColorMap:[],
      mouseoverSubjects: null,
      statisticalTestResults: {},
      tagNames: [],
      colorTag: null,
      symbolTag: null,
      colorGroups: [],
      symbolGroups: [],
      generatingReport: false,
      csvImportedData: [],
      importedDataIsLoaded: false,
      projectResults: [],
      loadingProjectResults: true
    }
  },
  mounted() {
    this.availableReportOptions = reportTemplateHandler.getScenarioAvailableTemplates(this.$store.state.paramsData.project.scenario)
  },
  async created() {
    this.projectResults = await Api.getProjectResults(this.project.id)
    this.loadingProjectResults = false
    this.mainGroup = this.$store.state.trialToShowInResultsCrumb
  },
  methods: {
    goToResultDashboard(result) {
      let subjectTemplateId = result.subject.templateId
      let trialId = result.trialId
      let projectId = result.trialMeta.project
      this.$store.state.trialToShowInResultsCrumb = `trial#${trialId}`
      this.$router.push({path: `/project/${projectId}/results/trial/${trialId}/subject/${subjectTemplateId}`})
    },
    // setRandomParamsFilter(){
    //   this.$modal.hide('selection-params')
    //   this.selection = this.resultsWithoutNullAttributes.filter((r)=>Math.random()>0.8)
    // },
    toOptions(arr){
      return arr.map((v)=>{
        return {
          label: v?(v.replace('scalars.','').replace('subject.templateId','Subject')):'None',
          value: v
        }
      })
    },
    getResultsById(ids){
      let sel = ids.map((e)=>this.resultsWithoutNullAttributes.find((d)=>d.subject.id===e))
      return sel
    },
    setDetailsLayout(){
      let bounds = this.$el.getBoundingClientRect()
      let w = Math.min(bounds.width-40,Math.max(640,bounds.width*0.4))
      let h = Math.min(bounds.height-40,Math.max(540,bounds.height*0.3))
      let x = bounds.width-20-w
      let y = 80
      this.detailsPopupLayout = {x,y,w,h}
    },
    openParamsSelection () {
      this.brushMode = null;
      this.$modal.show('selection-params', {project: this.project});
    },
    openCreateGroupModal () {
      this.$modal.show('create-group', {
        project: this.project,
        selection: this.selection
      })
    },
    refreshGroups(newGroup) {
      this.selection=null
      this.brushMode = null
      if(newGroup) {
        this.mainGroup = newGroup.id
        this.availableGroups.push(newGroup)
      } else {
        this.mainGroup = -1
      }
      this.$asyncComputed.availableGroups.update();
    },
    deleteGroup() {
      if(this.mainGroup != -1) {
        Api.deleteGroup(this.project, this.mainGroup).then(resp => {
          this.mainGroup = -1
          this.refreshGroups()
        })
      }
    },
    groupById(id) {
      return this.availableGroupsWithAll.find(e => e.value == id)
    },
    getSubjectActiveGroups (r) {
      let g = this.mainGroup == -1 ? [-1] : []
      let allGroups = this.compareGroups.concat(this.mainGroup == -1 ? [] : this.groupById(this.mainGroup))
      g =  g.concat(allGroups.filter(g => (g.subjectIds && g.subjectIds.includes(r.subject.id)) || g.value == -1).map(g => g.value))
      if (allGroups.find(e => e.id == 'trial#'+ r.trialId)) {
        g.push('trial#'+ r.trialId)
      }
      return g
    },
    applyFilter(filterConfig) {
      /// Pre-select subjects in the frontend according to filter specifications
      function between(x, min, max) {
        return x >= min && x <= max
      }
      let filteredResults = this.resultsWithoutNullAttributes
      if (filterConfig.population_general_characteristics && filterConfig.population_general_characteristics.length > 0) {
        filterConfig.population_general_characteristics[0].fields.forEach((filterCharacteristic) => {
          if (filterCharacteristic.valueType == 'boolean') {
            filteredResults = filteredResults.filter(result => {
              let subjectCharacteristicValue = result.subject.general_characteristics.find(subjectCharacteristic => subjectCharacteristic.id == filterCharacteristic.id).value[0]
              return filterCharacteristic.value.includes(subjectCharacteristicValue)
            })
          }
          else {
            filteredResults = filteredResults.filter(result => {
              let subjectCharacteristicValue = result.subject.general_characteristics.find(subjectCharacteristic => subjectCharacteristic.id == filterCharacteristic.id).value
              return between(subjectCharacteristicValue, filterCharacteristic.value[0], filterCharacteristic.value[1])
            })
          }
        })
      }
      if (filterConfig.resultScalars && filterConfig.resultScalars.length > 0) {
        filterConfig.resultScalars.forEach(filterScalar => {
          filteredResults = filteredResults.filter(result => {
            let subjectScalarValue = result.scalars.find(subjectScalar => subjectScalar.name == filterScalar.name).value
            return between(subjectScalarValue, filterScalar.fields[0].value[0], filterScalar.fields[0].value[1])
          })
        })
      }
      this.selection = filteredResults
    },
    async importCSVFile() {
      let csvFile = document.getElementById("csvFile")
      const input = csvFile.files[0];
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          this.csvImportedData = await d3.csvParse(text);
        } catch {
          alert("We're sorry, there was an error loading your data. Please check your file or try again later.")
        }
      }
      reader.readAsText(input)
    },
    generateProjectSubjectsFromCSV() {
      /// converts every subject in the csv to a results object like the one used in the project
      let projectResultObject = JSON.parse(JSON.stringify(this.resultsWithoutNullAttributes[0]))
      let projectTags = projectResultObject.tags
      let projectScalars = projectResultObject.scalars
      //let projectSparkline = resultObject.sparkline

      this.csvImportedData.forEach((importedSubject,index) => {
        let importedSubjectTags = JSON.parse(JSON.stringify(projectTags))
        importedSubjectTags.forEach(tag => {
          let tagName = tag.name
          if(importedSubject[tagName]) tag.value = importedSubject[tagName]
          else tag.value = 'Not provided'
        })
        let importedSubjectScalars = JSON.parse(JSON.stringify(projectScalars))
        importedSubjectScalars.forEach(scalar => {
          let scalarName = scalar.name
          if(importedSubject[scalarName]) scalar.value = parseFloat(importedSubject[scalarName])
          else scalar.value = 'Not provided'
        })
        let importedSubjectObject = JSON.parse(JSON.stringify(projectResultObject))
        importedSubjectObject.tags = importedSubjectTags
        importedSubjectObject.scalars = importedSubjectScalars
        importedSubjectObject.trialId = importedSubject["Trial"]
        importedSubjectObject.trialMeta = {
          "id": importedSubject["Trial"],
          "name": "Imported Data",
          "project": 0
        }
        importedSubjectObject.subject.templateId = importedSubject["Subject"]
        importedSubjectObject.subject.id = 1000 + index
        importedSubjectObject["isImported"] = true
        this.results.push(importedSubjectObject)
      })
      this.importedDataIsLoaded = true
    },
    removeImportedData() {
      let originalResults = this.results.filter(result => !('isImported' in result))
      this.results = originalResults
      this.importedDataIsLoaded = false
    },
    exportCSV(){
      var group = this.currentMainGroup;
      if (typeof group.id == "string" && group.id.startsWith("trial"))
        CSVExportTrial(this.resultsWithoutNullAttributes, group.trialIds[0], this.project.id)
      else if(this.currentMainGroup.value === -1)
        CSVExportProject(this.resultsWithoutNullAttributes, this.project.id)
      else
        CSVExportGroup(this.resultsWithoutNullAttributes, group, this.project.id)
    },
    updateStatisticalAnalysis(statisticalTestResults) {
      this.statisticalTestResults = statisticalTestResults
    },
    generateColorGroups() {
      function uuidv4() {
        return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
          (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
      }
      let importedResults = this.results.filter(result => result.isImported)
      let simulationResults = this.resultsWithoutNullAttributes.filter(result => !result.isImported)
      if (this.colorTag) {
        // generate groups according to selected tag values (take into account imported results if present)
        let simulationColorGroups = _.groupBy(simulationResults, result => result.tags.find(tag => tag.name === this.colorTag).value)
        let importedColorGroups = _.groupBy(importedResults, result => result.tags.find(tag => tag.name === this.colorTag).value)
        // add (i) to imported groups
        let importedColorGroupsWithDescription = {}
        for(let key in importedColorGroups) {
            if(importedColorGroups.hasOwnProperty(key)) {
                importedColorGroupsWithDescription['i' + key] = importedColorGroups[key];
            }
        }
        this.colorGroups = {
          ...importedColorGroupsWithDescription,
          ...simulationColorGroups
        }
      } else {
        if (importedResults.length) {
          this.colorGroups = {
            'Simulation results': simulationResults,
            'Imported results': importedResults
          }
        } else {
          this.colorGroups = {}
          this.results.forEach(subject => subject.tagColor = '#1e275b')
        }
      }
      // fill color groups with supplementary info
      let colorSet = []
      let colorGroupsArray = Object.entries(this.colorGroups)
      colorGroupsArray.forEach((group,idx) => {
        let groupName = group[0]
        let groupResults = group[1]
        groupResults.forEach(subject => subject.tagColor = COMPARE_COLORS[idx])
        colorSet.push({
          key: uuidv4(),
          name: groupName,
          color: COMPARE_COLORS[idx],
          paintRegression: groupName == 'Not provided' ? false : true,
          subjects: groupResults,
          isImported: groupResults[0].isImported ? true : false
        })
      })
      this.colorGroups = colorSet
      /// add ALL group for global regression
      this.colorGroups.splice(0, 0, {
        key: 'all',
        name: 'Total',
        color: '#1e275b',
        paintRegression: this.colorTag ? false : true,
        subjects: this.resultsWithoutNullAttributes
      })
    },
    async updateSymbols() {
      if(this.symbolTag == null) {
        this.resultsWithoutNullAttributes.forEach(result => result.tagSymbol = COMPARE_SYMBOLS[0])
        this.symbolGroups = []
      } else {
        // generate groups according to selected tag values
        let symbolGroups = _.groupBy(this.resultsWithoutNullAttributes, result => result.tags.find(tag => tag.name === this.symbolTag).value)
        let tagGroups = Object.keys(symbolGroups)
        // classify tag color according to the value of the selected tag
        this.resultsWithoutNullAttributes.forEach((result,resultIdx) => {
          tagGroups.forEach((group,idx) => {
            if(result.tags.find(tag => tag.name === this.symbolTag).value === group) this.resultsWithoutNullAttributes[resultIdx].tagSymbol = COMPARE_SYMBOLS[idx]
          })
        })
        // generate color groups for the legend
        let symbolSet = []
        tagGroups.forEach((groupName,idx) => {
          symbolGroups[groupName].forEach(subject => {
            subject.symbol = COMPARE_SYMBOLS[idx]
          })
          symbolSet.push({
            key: groupName,
            name: groupName,
            symbol: COMPARE_SYMBOLS[idx],
            subjects: symbolGroups[groupName]
          })
        })
        this.symbolGroups = symbolSet
        await this.$nextTick()
        this.fillLegendSymbols(symbolSet)
      }
    },
    generateSymbol(symbolName) {
      if (symbolName == 'circle') return d3.symbolCircle
      else if (symbolName == 'cross') return d3.symbolCross
      else if (symbolName == 'square') return d3.symbolSquare
      else if (symbolName == 'triangle') return d3.symbolTriangle
      else if (symbolName == 'diamond') return d3.symbolDiamond
      else if (symbolName == 'wye') return d3.symbolWye
      else if (symbolName == 'star') return d3.symbolStar
    },
    fillLegendSymbols(symbolSet) {
      symbolSet.forEach(group => {
        let groupSymbol = d3.symbol()
          .type(this.generateSymbol(group.symbol))
          .size(50)
        d3.select(`.legend-icon-${group.symbol}`)
          .append("path").attr("class","path")
          .attr("fill", "white")
          .attr("stroke", "grey")
          .attr("stroke-width", "1")
          .attr("transform", "translate(5,5)")
          .attr("d", groupSymbol)
          .attr("opacity",1)
      })
    },
    showOrHideRegression(colorGroup) {
      let groupIndex = this.colorGroups.findIndex(group => group.key === colorGroup.key)
      this.colorGroups.splice(groupIndex, 1, this.colorGroups[groupIndex] = {
        ...this.colorGroups[groupIndex],
        paintRegression: colorGroup.paintRegression ? false : true
      })
      // deactivate all regressions when global regression is activated
      if (groupIndex === 0 & !colorGroup.paintRegression) {
        for (let i = 1; i < this.colorGroups.length; i++) {
          this.colorGroups.splice(i, 1, this.colorGroups[i] = {
            ...this.colorGroups[i],
            paintRegression: false
        })
        }
      }
    },
    // REPORT METHODS. The current reporting feature is temporary and should be replaced in the future to achieve more complex reporting.
    async generateReportPlots(plotOptionsTemplate) {
      /// Generates plots by changing parameters in the scatter plot
      let plots = []
      let plotSizes = []
      let regressions = []
      let regressionSizes = []
      let colorLegends = []
      let colorLegendSizes = []
      let oldXattr = this.xAttr
      let oldYAttr = this.yAttr
      let oldColorTag = this.colorTag
      for (let i=0; i<plotOptionsTemplate.plotCount; i++) {
        this.xAttr = plotOptionsTemplate.xAttrs[i]
        this.yAttr = plotOptionsTemplate.yAttrs[i]
        this.colorTag = plotOptionsTemplate.colorTags[i]
        await new Promise(resolve => setTimeout(resolve, 400))
        // Hide unselected regressions
        plotOptionsTemplate.regressionGroupsToHide[i].forEach(groupToHide => {
          if (groupToHide != [null]) {
            let groupIndex = this.colorGroups.findIndex(colorGroup => colorGroup.name == groupToHide)
            if (groupIndex != -1) {
              this.colorGroups.splice(groupIndex, 1, this.colorGroups[groupIndex] = {
                ...this.colorGroups[groupIndex],
                paintRegression: false
              })}
          }
        })
        // Capture elements
        let plotNode = document.getElementById('plot-screenshot')
        plotSizes[i] = [plotNode.clientWidth, plotNode.clientHeight]
        let regressionNode = document.getElementById('regression-image')
        regressionSizes[i] = [regressionNode.clientWidth, regressionNode.clientHeight]
        plots[i] = await domtoimage.toPng(plotNode,{style: {backgroundColor: '#DADEE0'}})
        regressions[i] = await domtoimage.toPng(regressionNode,{style: {backgroundColor: 'white'}})
        let colorLegendNode = document.getElementById('color-legend')
        colorLegendSizes[i] = [colorLegendNode.clientWidth, colorLegendNode.clientHeight]
        colorLegends[i] = await domtoimage.toPng(colorLegendNode,{style: {backgroundColor: 'white'}})
        // Return regressions to old state
        plotOptionsTemplate.regressionGroupsToHide[i].forEach(groupToHide => {
          if (groupToHide != null) {
            let groupIndex = this.colorGroups.findIndex(colorGroup => colorGroup.name == groupToHide)
            if (groupIndex != -1) {
              this.colorGroups.splice(groupIndex, 1, this.colorGroups[groupIndex] = {
              ...this.colorGroups[groupIndex],
              paintRegression: true
            })
            }
          }
        })
      }
      // Return plot to old state
      this.xAttr = oldXattr
      this.yAttr = oldYAttr
      this.colorTag = oldColorTag
      return {
        plots: plots,
        pSizes: plotSizes,
        regressions: regressions,
        rSizes: regressionSizes,
        colorLegends: colorLegends,
        colorLegendSizes: colorLegendSizes,
      }
    },
    async generateCustomReport(plotOptions) {
      plotOptions = {
        ...plotOptions,
        plotCount: plotOptions.xAttrs.length,
      }
      this.generateReport(plotOptions)
    },
    async generateReport(plotOptions) {
      this.$modal.show('loading-modal', {
        title: 'Generating report',
        subtitle: 'It may take a few seconds'
      })
      this.generatingReport = true
      let organisation = await Api.getUser()
      let images = await this.generateReportPlots(plotOptions)
      // Currently hardcoded for ITQT
      let allConcentrations = this._data.results.map(result => result.tags[7].value.toString())
      let concentrations = [... new Set(allConcentrations)]
      let allAnatomies = this._data.results.map(result => result.tags[0].value.toString())
      let anatomies = [... new Set(allAnatomies)]
      let reportData = {
          'summary': this.project.fields[0].value.toString().replace(/<[^>]+>/g, ''),
          'project_type': this.project.fields[1].value.toString(),
          'study_phase': this.project.fields[2].value.toString(),
          'external_identifier': this.project.fields[3].value.toString(),
          'primary_investigator': this.project.fields[4].value.toString(),
          'outcome': this.project.fields[5].value.toString(),
          'description_long': this.project.fields[6].value.toString().replace(/<[^>]+>/g, ''),
          'study_design': this.convertHTMLtoPlainText(this.project.fields[7].value),//
          'elegibility_criteria': this.convertHTMLtoPlainText(this.project.fields[8].value),//this.project.fields[8].value.toString(),
          'organisation': 'Boehringer Ingelheim',
          'title': this.project.name,
          'description': this.project.description,
          'drug': this._data.results[0].tags[8].value.toString().toUpperCase(),
          'concentrations': concentrations.map(parseFloat).sort((a,b)=>a-b).flat().toString().replaceAll(',',' - '),
          'anatomies': anatomies.length.toString(),
          'subjects': this._data.results.length.toString(),
          'user': organisation.full_name,
      }
      this.$modal.hide('loading-modal')
      reportGenerator.generateReport(plotOptions, this.$store.state.paramsData.project.scenario, reportData, images)
      this.generatingReport = false
      /* var group = this.currentMainGroup;
      if (typeof group.id == "string" && group.id.startsWith("trial"))
        Api.downloadTrialReport({trial: group.trialIds[0]});
      else
        Api.downloadGroupReport({group: group.id}); */
    },
    openCustomReportModal() {
      let colorGroupsPerTag = {}
      this.tagNames.forEach(tagName => colorGroupsPerTag[tagName] = _.groupBy(this.resultsWithoutNullAttributes, result => result.tags.find(tag => tag.name === tagName).value))
      this.$modal.show('modal-custom-report', {colorGroupsPerTag: colorGroupsPerTag})
    },
    convertHTMLtoPlainText(html) {
      var tempDivElement = document.createElement("div");
      tempDivElement.innerHTML = html;
      return tempDivElement.textContent || tempDivElement.innerText || "";
    },
  },
  asyncComputed: {
    mainResults () {
      if(this.project && !this.loadingProjectResults) {
        if(!this.mainGroup || this.mainGroup===-1) return this.projectResults //equivalente a this.$route.params.trial
        else {
          if (typeof this.mainGroup == 'string' && this.mainGroup.includes('trial#')) {
            return this.projectResults.filter(result => result.trialId == this.currentMainGroup.trialIds)
          } else if (typeof this.mainGroup == 'number') {
            return this.projectResults.filter(result => this.currentMainGroup.subjectIds.includes(result.subject.id))
          } else return []
        }
      }
      return []
    },
    compareResults () {
      if(this.compareGroupsId.length > 0){
        return Promise.all(
          this.compareGroups.map((g) => {
            let filter = {}
            if (g.value != -1) {
              filter = JSON.parse(JSON.stringify(g))
              //delete filter.label
              //delete filter.value
            }
            return Api.getProjectResults(this.project.id, filter).then((responses)=>{
              return responses
            })
          })
        )
      }
      else {
        return []
      }
    },
    availableGroups: {
      get () {
        if(this.project) {
          return Api.getGroups(this.project)
        }
        return []
       },
       default: []
    },
    results () {
      return this.mainResults && _.unique(this.mainResults.concat(this.compareResults ? this.compareResults.flat() : []), 
          el => el.subject.id
        ).map(s => {
          return  {
            ...s,
            // assign a symbol and a color to every result
            tagColor: COMPARE_COLORS[0],
            tagSymbol: COMPARE_SYMBOLS[0], 
            groups: this.getSubjectActiveGroups(s)
          }
        })
    },
  },
  computed:{
    resultsWithoutNullAttributes() {
      if (this.results) {
        let resultsWithoutNullAttributes = this.results.filter(subject => {
          let xAttrIsNull, yAttrIsNull
          if (this.xAttr != 'subject.templateId') {
            xAttrIsNull = subject.scalars.find(scalar => scalar.name == this.xAttr.split('scalars.')[1]).value == null ? true : false
          } else {
            xAttrIsNull = false
          }
          if (this.yAttr != 'subject.templateId') {
            yAttrIsNull = subject.scalars.find(scalar => scalar.name == this.yAttr.split('scalars.')[1]).value == null ? true : false
          } else {
            yAttrIsNull = false
          }
          let containsNullAttr = xAttrIsNull || yAttrIsNull
          if (containsNullAttr) return false // discard subjects with null attribute
          else return true // get subjects with null attribute
        })
        return resultsWithoutNullAttributes        
      } else {
        return []
      }
    },
    resultsWithNullAttribute() {
      if (this.results) {
        let resultsWithNullAttributes = this.results.filter(subject => {
          let xAttrIsNull, yAttrIsNull
          if (this.xAttr != 'subject.templateId') {
            xAttrIsNull = subject.scalars.find(scalar => scalar.name == this.xAttr.split('scalars.')[1]).value == null ? true : false
          } else {
            xAttrIsNull = false
          }
          if (this.yAttr != 'subject.templateId') {
            yAttrIsNull = subject.scalars.find(scalar => scalar.name == this.yAttr.split('scalars.')[1]).value == null ? true : false
          } else {
            yAttrIsNull = false
          }
          let containsNullAttr = xAttrIsNull || yAttrIsNull
          if (containsNullAttr) return true // get subjects with null attribute
          else return false // discard subjects without null attribute
        })
        return resultsWithNullAttributes      
      } else {
        return []
      }
    },
    colorsMap () {
      return [COLORS.mainGroup].concat(this.compareGroupsId.map((v, i) => COMPARE_COLORS[i]))
    },
    isLoading(){
      let loadingMain = this.mainGroup!==undefined && this.$asyncComputed.mainResults.updating
      let loadingOthers = this.compareGroupsId.length > 0 && this.$asyncComputed.compareResults.updating
      let resultsEmpty = this.results && this.results.length < 1
      return loadingMain || loadingOthers || resultsEmpty || this.loadingProjectResults
    },
    heatMapScale(){
      let attr = this.colorAttr
      let scale = d3.scaleSequential(d3.interpolateWarm)
      if(this.resultsWithoutNullAttributes) scale.domain(d3.extent(this.resultsWithoutNullAttributes,(d)=>getNestedAttr(d,this.colorAttr).value))
      return scale
    },
    color () {
      var self = this
      if(!this.compareResults || !this.colorAttr) return (d)=>COLORS.mainGroup
      return (d)=>{
        if(this.colorAttr && this.chartMode === 'heatmap') {
          return this.heatMapScale(getNestedAttr(d, this.colorAttr).value)
        } else {
          let groupsKey = d.groups.join('_')
          let g = self.groupsAndColors.find(el => el.key == groupsKey)
          return g ? g.color : COLORS.mainGroup
        }
      }
    },
    alpha_legendScale(){
      let c1 = d3.color(COLORS.mainGroup)
      let c2 = d3.color(COLORS.mainGroup)
      c1.opacity = 0.2 //deberia ser 0.3, peroqueda mejor con 0.2...
      c2.opacity = 1
      return d3.scaleSequential(d3.interpolate(c1,c2))
        .domain(d3.extent(this.resultsWithoutNullAttributes,(d)=>getNestedAttr(d,this.alphaAttr).value))
    },
    color_legendScale(){
      let attr = this.colorAttr
      return this.heatMapScale
    },
    size_legendScale(){
      let attr = this.sizeAttr
      return d3.scaleLinear().range([2,8]).domain(d3.extent(this.resultsWithoutNullAttributes,(d)=>getNestedAttr(d,this.sizeAttr).value))
    },
    _availableAttrs(){
      let scalars = (this.results && this.results.length > 0 && this.results[0].scalars)?this.results[0].scalars.map((s)=>s.name):[]
      return scalars.map((attr)=>'scalars.'+attr)
    },
    availableAttrs(){
      let scalars = (this.results && this.results.length > 0 && this.results[0].scalars)?this.results[0].scalars.map((s)=>s.name):[]
      return this.toOptions(scalars.map((attr)=>'scalars.'+attr))
    },
    availableAttrsWithGender(){
      return this.availableAttrs //['Gender', 'Trials'].concat(this.availableAttrs)
    },
    availableAttrsWithNull (){
      return this.toOptions([null].concat(this._availableAttrs))
    },
    availableAttrsX (){
      return this.toOptions(['subject.templateId'].concat(this._availableAttrs))
    },
    availableAttrsY (){
      return this.toOptions(this._availableAttrs)
    },
    groupedResults () {
      // crear el dictionari de patient
      let tmp = (this.results || []).reduce((dict,res)=>{
        dict[res.subject.templateId] = dict[res.subject.templateId] || {
          id: res.subject.templateId,
          subject:res.subject,
          cases:[],
          isGroup: true,
        }
        dict[res.subject.templateId].cases.push({...res,isChild:true})
        return dict
      },{})
      let patientsResults = Object.values(tmp)
      let globalPatientsResults = patientsResults.map(patient => {
        let n = patient.cases.length
        let totals = patient.cases.reduce((sums,_case)=>{
          (_case.scalars || []).forEach((s)=>{
            if (s.value != null) {
              let k = s.name
              if(!sums[k]) sums[k]=[]
              // sums[g]+=+_case.global[g]
              sums[k].push(_case.scalars.find((ss)=>ss.name===k))
            }
          })
          return sums
        },{})
        let groupedScalars = Object.keys(totals).map((k)=>{
          let m = mean(totals[k].map((d)=>d.value))
          let s = std(totals[k].map((d)=>d.value))
          return {name:k,mean:m,std:s,value:m,unit:totals[k][0].unit}
        })

        return { ...patient, scalars: groupedScalars}
      })
      return globalPatientsResults
    },
    sortedGroupedResults() {
      let sign = this.sortingDirection === 'down' ? 1 : (-1)
      if(this.sortAttr === 'Gender') {
        let sortAttrAux = this.sortAttr.toLowerCase()
        this.groupedResults.sort((a,b) => {
          return sign * (getNestedAttr(a, sortAttrAux) - getNestedAttr(b, sortAttrAux))
        })
      } else if(this.sortAttr === 'Trials') {
        let sortAttrAux = 'cases'
        this.groupedResults.sort((a,b) => {
          return sign * (getNestedAttr(a, sortAttrAux).length - getNestedAttr(b, sortAttrAux).length)
        })
      } else {
        this.groupedResults.sort((a,b) => {
          let va = getNestedAttr(a,this.sortAttr)
          if(va) va = va.mean
          let vb = getNestedAttr(b,this.sortAttr)
          if(vb) vb = vb.mean
          return sign * (va - vb)
        })
      }
      return [...this.groupedResults]
    },
    availableGroupsAndTrials() {
        let groupsAndTrials = []

        groupsAndTrials = groupsAndTrials.concat(
            this.project.trials.map(t => {
            return {
              value: {id: t.id, type: "trial"}, 
              type: "trial",
              id: "trial#" + t.id,
              label: t.name,
              name: t.name
            }
          })
        )

        groupsAndTrials = groupsAndTrials.concat(
            this.availableGroups.map(g => {
            return {
              ...g,
              type: "group",
              value: {id: g.id, type: "group"},
              label: g.name
            }
          })
        )

        /* if (this.importedDataIsLoaded) {
          groups.push({
            value: 'Imported Data',
            id: 'trial#imported',
            label: 'Imported Data',
            name: 'Imported Data',
            trialIds: [0],
            value: 'trial#imported',
            preventDelete: false
          })
        }  */

        return groupsAndTrials
    },
    availableGroupsWithAll() {
      let groups = [{value:-1, id: -1, label:'All', name:'All', preventDelete: true}]
      groups = groups.concat(this.project.trials.map(t =>{
        return {
          value: 'trial#' + t.id,
          id: 'trial#' + t.id,
          label: t.name,
          name: t.name,
          trialIds: [t.id],
          preventDelete: true
        }
      }))
      /* if (this.importedDataIsLoaded) {
        groups.push({
          value: 'Imported Data',
          id: 'trial#imported',
          label: 'Imported Data',
          name: 'Imported Data',
          trialIds: [0],
          value: 'trial#imported',
          preventDelete: false
        })
      } */
      groups = groups.concat(this.availableGroups.map(el =>{
        return {
          ...el,
          value: el.id,
          label: el.name,
        }
      }))
      return groups
    },
    currentMainGroup() {
      return this.availableGroupsWithAll.find(el =>el.id == this.mainGroup)
    },
    availableCompareGroups() {
      return this.availableGroupsWithAll.filter(el => el.value != this.mainGroup)
    },
    compareGroups() {
      return this.compareGroupsId.map(g => this.groupById(g))
    },
    showRegression() {
      return this.xAttr != 'subject.templateId'
    }
  },
  watch:{
    mainGroup () {
      this.compareGroupsId = []
      this.$store.state.trialToShowInResultsCrumb = this.mainGroup
    },
    results() {
      if(!this.isLoading) this.tagNames = this.results[0].tags.map(tag => tag.name)
      this.generateColorGroups()
    },
    brushMode(){
      // this.selection = null
      // if(this.brushMode==='params') this.$modal.show('selection-params')
    },
    size(){
      this.setDetailsLayout()
    },
    detailsOpen(){
      this.setDetailsLayout()
    },
    chartMode(){
      // this.selection = null
      this.brushMode = null
    },
    mainResults(){
      if(this.mainResults && this.mainResults.length > 0){
        this.$asyncComputed.results.update()
        if(this.mainResults[0].scalars.length > 1) {
          this.xAttr = 'scalars.' + this.mainResults[0].scalars[0].name
          this.yAttr = 'scalars.' + this.mainResults[0].scalars[1].name
          this.sortAttr = 'scalars.' + this.mainResults[0].scalars[0].name
          this.colorAttr = 'scalars.' + this.mainResults[0].scalars[1].name
        }
      }
    },
    compareResults () {
      this.$asyncComputed.results.update() 
    },
    symbolTag() {
      this.updateSymbols()
    },
    colorTag() {
      this.generateColorGroups()
    },
    xAttr() {
      this.generateColorGroups()
    },
    yAttr() {
      this.generateColorGroups()
    },
    csvImportedData() {
      this.generateProjectSubjectsFromCSV()
    }
  },
  beforeDestroy() {
    if (this.$route.name != 'SubjectResultsView') this.$store.state.trialToShowInResultsCrumb = -1
  }
}
</script>

<style lang="stylus" scoped>
.project-results
  display: flex
  flex: 1 1 0%
  flex-direction: column
  overflow: hidden
  height 100%
  // padding-bottom: 60px // por el .floating-panel
  position: relative

  .header
    display: flex
    // flex: 0 0 auto
    width: 100%

    .controls-top
      align-items: center
      border-bottom: solid 2px #fff
      display: flex
      min-height: 70px
      padding-left: 10px
      padding-right: 10px
      width: 100%

      .mode-btns
        

        .mode-label
          color: $shuttle-gray
          m-font-size(12, 14)
          margin-bottom: 4px
        .btns-container
          align-items: center
          display: flex
        .mode-btn
          align-items: center
          background-color: #fff
          border: 1px solid #fff
          border-radius: 16px
          box-shadow: 5px 5px 20px rgba($lynch, 0.25)
          cursor: pointer
          display: flex
          height: 32px
          // padding: 10px 12px

          &:hover
            border-color: $heather

          &:not(:last-child)
            margin-right: 10px

          &.selected
            background-color: transparent
            border-color: #000
            cursor: default

            > label
              cursor: default

          > input[type="radio"]
            display: none

          > label
            m-font-size(13, 16)
            cursor: pointer
            text-transform: capitalize
            white-space: nowrap
            padding: 10px 12px

      .selection-modes
        min-width 100px
        .selection-label
          color: $shuttle-gray
          m-font-size(12, 14)
          margin-bottom: 4px
        .selection-container
          align-items: center
          display: flex
          min-height 30px
        .selector
          margin-right: 20px

        .selection-btn
          cursor: pointer

          &.rect
            m-icon("rect", 20)

          &.poly
            m-icon("poly", 20)

          &.params
            m-icon("filter", 20)

          &.innactive
            opacity: 0.4

        .create-group-btn
          margin-right: 20px
          max-width 150px
        .deselect
          margin-left -9px

      .group-selector
        align-items: center
        display: flex

        .multi-select-component
          display: flex
          flex-direction column
          width: 250px
          +m-breakpoint(sm lg)
            width 120pxopenCustomReportModal
      .align-right
        margin-left auto
        display flex

      .sort-selector
        align-items: center
        display: flex
        margin-top 16px
        margin-left 10px
        .multi-select-component
          width: 160px
          +m-breakpoint(sm lg)
            width 120px

        .sort-arrow
          background-image: url("~@/assets/icons/icon-arrow.svg")
          background-position: center center
          background-repeat: no-repeat
          background-size: contain
          height: 15px
          margin-left: 5px
          transform-origin: center center
          transition: transform 0.25s ease-in-out
          width: @height

          &.up
            transform: rotate(180deg)

          &.unselected
            opacity: 0.3

            &:hover
              cursor: pointer
              opacity: 1

      .details-button
        cursor: pointer
        margin-left 15px
        margin-top 20px

      .add-config-popover
        display:flex

        &:hover
          .add-config-item
              background-color: rgba($titan-white, 0.4)
        .trigger
          width 100%

        .option-popover
          width 400px
          max-height 400px
          background $titan-white
          overflow auto
          padding 15px

  .results-body
    display: flex
    flex: 1 1 0%
    overflow: hidden

  .separator
    background-color: #fff
    height: 40px
    margin-left: 10px
    margin-right: 10px
    width: 1px

  .left-wrapper
    display: flex
    flex: 1 1 0%
    flex-direction: column
    overflow: hidden

    .content
      display: flex
      flex: 1 1 0%
      overflow: hidden
      position: relative

      .trials-scatter
        flex: 1 1 0%
        // overflow: hidden

        >>> svg
          display: flex

      .result-patient-list
        width: 100%

      .loader
        background: rgba(255, 255, 255, 0.3)
        height: 100%
        left: 0
        position: absolute
        top: 0
        width: 100%

        .text
          left: 50%
          position: absolute
          top: 50%
          transform: tranlsate(-50%, -50%)

  .right-wrapper
    border-left: solid 2px #fff
    display: flex
    flex: 0 0 25%
    flex-direction: column
    padding: 15px
    width: 25%
    overflow: auto;
    .main-group-title
      display: flex
      padding: 10px
      m-font-size(22, 26)
      border-bottom: solid 1px #fff
      font-weight: bold

      .group-info
        color: $shuttle-gray
        font-weight: $fw-regular
        m-font-size(13, 16)
      .title
        flex: 1 1 auto

      .icon
        m-icon("toggle-panel-caret", 20)

    .control-block
      border-bottom: solid 1px #fff
      padding: 10px

      &.half-selectors
        .selector
          width: 50%

          &:not(:last-child)
            margin-right: 5px
  
    .attr-selectors
      display: flex

      .selector
        display: flex
        flex-direction: column

    .selector
      pointer-events: all
      width: 100%

      &.dropdown
        display: flex

    .compare-legend
      display: flex
      flex-direction: column
      margin-top: 5px
      padding-left: 15px
      padding-right: 15px

      .legend-item
        align-items: center
        color: $shuttle-gray
        display: flex

        &:not(:last-child)
          margin-bottom: 5px

        .legend-icon
          border-radius: 10px
          height: 10px
          width: 10px
          max-height: 10px
          max-width: 10px
          min-height: 10px
          min-width: 10px

        .legend-label
          margin: 0 10px

        .legend-count
          flex: 1 1 0%
          text-align: end

    .null-attributes-container
      background-color: rgba(255,0,0,0.1)
      display: flex
      flex-direction: column
      margin-top: 5px
      padding: 10px
      m-font-size(12, 14)
      color: $shuttle-gray

      .subjects
        margin-top: 5px
        display: flex
        flex-direction: column
        max-height: 70px
        overflow-y: auto
        .subject
          &:hover
            cursor: pointer
            color: white

    .regression-container
      display: flex
      flex-direction: column
      margin-top: auto

    #regression-image
      padding-top: 10px
      padding-bottom: 20px
      padding-left: 15px
      padding-right: 15px
      border-top: solid 1px #fff

      .title 
        font-weight: bold
        m-font-size(18, 20)
        margin-bottom: 5px

      .group-name
        color: black
        font-weight: $fw-regular
        m-font-size(15, 17)
        margin-bottom: 20px

      .regression-item
        align-items: center
        display: flex
        margin-top: 3px
        margin-bottom: 3px
        justify-content: space-between

        .label
          m-font-size(14,16)
          text-align: center

          &.name
            width: 15%
          &.equation
            width: 75%
          &.r2
            width: 10%

        .item
          font-weight: $fw-regular
          color: $shuttle-gray
          m-font-size(16, 18)
          text-align: center
          &.equation
            width: 75%
          &.r2
            width: 10%
          &.icon
            margin: 0 auto
            .base-circle
              background-color: white
              height: 16px
              width: 16px
              border-radius: 50%
              border: black
              position: relative
              border-style: solid
              border-width: 1px
              border-color: #adadad
              .selected-option-mark
                height: 10px
                width: 10px
                border-radius: 50%
                position: absolute
                top: 50%
                left: 50%
                margin: -5px 0px 0px -5px
          

  // .draggable
  // border none
  // box-shadow 0 0 10px 0px rgba(0,0,0,0.3);
  // >>> .handle
  // opacity 0
  // width 20px
  // height 20px
  // >>> .floating-panel
  // overflow: hidden;
    
  .option-popover
    .import-paragraph
      font-weight: $fw-bold

  .selection-params
    .filters-modal-content
      text-align: center
</style>